import { createHmac, randomBytes, timingSafeEqual } from "crypto";

const TICKET_PREFIX = "gencow_ws1";
const DEFAULT_TTL_SECONDS = 60;
const MAX_TTL_SECONDS = 120;
const MAX_TICKET_LENGTH = 4096;

export type RealtimeTicketFailure =
  | "malformed"
  | "bad_signature"
  | "app_mismatch"
  | "expired"
  | "invalid_payload";

export interface CreateRealtimeTicketInput {
  appName: string;
  userId: string;
  secret: string;
  sessionId?: string | null;
  ttlSeconds?: number;
  nowMs?: number;
  nonce?: string;
}

export interface VerifyRealtimeTicketInput {
  appName: string;
  secret: string;
  nowMs?: number;
}

export type VerifyRealtimeTicketResult =
  | {
      ok: true;
      appName: string;
      userId: string;
      sessionId: string | null;
      nonce: string;
      issuedAt: number;
      expiresAt: number;
    }
  | { ok: false; reason: RealtimeTicketFailure };

function encodeBase64Url(value: string): string {
  return Buffer.from(value, "utf8").toString("base64url");
}

function decodeBase64Url(value: string): string | null {
  try {
    return Buffer.from(value, "base64url").toString("utf8");
  } catch {
    return null;
  }
}

function signSegment(segment: string, secret: string): string {
  return createHmac("sha256", secret).update(segment).digest("base64url");
}

function safeEqual(a: string, b: string): boolean {
  const left = Buffer.from(a);
  const right = Buffer.from(b);
  return left.length === right.length && timingSafeEqual(left, right);
}

function validateRequiredString(value: string, label: string): void {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${label} required`);
  }
}

function resolveTtlMs(ttlSeconds?: number): number {
  const ttl = Number.isFinite(ttlSeconds) && ttlSeconds! > 0 ? ttlSeconds! : DEFAULT_TTL_SECONDS;
  return Math.floor(Math.min(ttl, MAX_TTL_SECONDS) * 1000);
}

function randomNonce(): string {
  return randomBytes(16).toString("base64url");
}

export function createRealtimeTicket(input: CreateRealtimeTicketInput): string {
  validateRequiredString(input.appName, "appName");
  validateRequiredString(input.userId, "userId");
  validateRequiredString(input.secret, "secret");

  const nowMs = input.nowMs ?? Date.now();
  const payload = {
    v: 1,
    appName: input.appName,
    userId: input.userId,
    sessionId: input.sessionId ?? null,
    iat: nowMs,
    exp: nowMs + resolveTtlMs(input.ttlSeconds),
    nonce: input.nonce ?? randomNonce(),
  };
  const payloadSegment = encodeBase64Url(JSON.stringify(payload));
  const signature = signSegment(payloadSegment, input.secret);
  return `${TICKET_PREFIX}.${payloadSegment}.${signature}`;
}

export function verifyRealtimeTicket(
  ticket: string,
  input: VerifyRealtimeTicketInput,
): VerifyRealtimeTicketResult {
  if (typeof ticket !== "string" || ticket.length === 0 || ticket.length > MAX_TICKET_LENGTH) {
    return { ok: false, reason: "malformed" };
  }
  validateRequiredString(input.appName, "appName");
  validateRequiredString(input.secret, "secret");

  const parts = ticket.split(".");
  if (parts.length !== 3 || parts[0] !== TICKET_PREFIX) return { ok: false, reason: "malformed" };
  const [, payloadSegment, signature] = parts;
  if (!safeEqual(signature, signSegment(payloadSegment, input.secret))) {
    return { ok: false, reason: "bad_signature" };
  }

  const decoded = decodeBase64Url(payloadSegment);
  if (!decoded) return { ok: false, reason: "malformed" };

  let payload: unknown;
  try {
    payload = JSON.parse(decoded);
  } catch {
    return { ok: false, reason: "malformed" };
  }

  if (!payload || typeof payload !== "object") return { ok: false, reason: "invalid_payload" };
  const record = payload as Record<string, unknown>;
  if (
    record.v !== 1 ||
    typeof record.appName !== "string" ||
    typeof record.userId !== "string" ||
    typeof record.nonce !== "string" ||
    typeof record.iat !== "number" ||
    typeof record.exp !== "number"
  ) {
    return { ok: false, reason: "invalid_payload" };
  }
  if (record.appName !== input.appName) return { ok: false, reason: "app_mismatch" };
  if ((input.nowMs ?? Date.now()) >= record.exp) return { ok: false, reason: "expired" };

  return {
    ok: true,
    appName: record.appName,
    userId: record.userId,
    sessionId: typeof record.sessionId === "string" ? record.sessionId : null,
    nonce: record.nonce,
    issuedAt: record.iat,
    expiresAt: record.exp,
  };
}
